/* @flow */

import * as React from 'react';
import glamorous from 'glamorous';
import { transparentize } from 'polished';
import FormLabel from 'app/components/form/FormLabel';

type Props = {
  cols?: number,
  disabled?: boolean,
  label?: string,
  lightContent?: boolean,
  maxLength?: number,
  name: string,
  onChange?: string => void,
  placeholder?: string,
  readOnly?: boolean,
  required?: boolean,
  rows?: number,
  size?: 'default' | 'large' | 'small',
  value: string,
};

const defaultProps = {
  cols: undefined,
  disabled: false,
  label: undefined,
  lightContent: false,
  maxLength: undefined,
  onChange: undefined,
  placeholder: '',
  readOnly: false,
  required: false,
  rows: undefined,
  size: 'default',
};

const TextAreaWrapper = glamorous.div(({ theme }) => ({
  marginBottom: theme.spacing.default,
}));

const TextField = glamorous('textarea', {
  filterProps: ['lightContent', 'size'],
})(({ theme }) => ({
  border: 'none',
  borderRadius: theme.borderRadius.default,
  boxShadow: 'none',
  display: 'block',
  fontWeight: theme.fontWeight.demiBold,
  outline: 'none',
  paddingLeft: theme.spacing.small,
  resize: 'vertical',
  width: '100%',
}), ({ size, theme }) => ({
  default: {
    fontSize: theme.fontSize.large,
    padding: theme.spacing.small,
  },
  small: {
    fontSize: theme.fontSize.medium,
    padding: theme.spacing.small,
  },
  large: {
    fontSize: theme.fontSize.xlarge,
    padding: theme.spacing.default,
  },
}[size]), ({ lightContent, theme }) => (
  lightContent ? {
    backgroundColor: theme.color.basis6,
    color: theme.color.basis1,
    '&::placeholder': {
      color: theme.color.basis4,
    },
    '&[disabled]': {
      backgroundColor: transparentize(0.5, theme.color.basis4),
      color: theme.color.basis4,
    },
    '&:focus': {
      backgroundColor: theme.color.basis5,
    },
  } : {
    backgroundColor: theme.color.basis2,
    color: theme.color.basis6,
    '&::placeholder': {
      color: theme.color.basis3,
    },
    '&[disabled]': {
      backgroundColor: transparentize(0.5, theme.color.basis4),
      color: theme.color.basis4,
    },
    '&:focus': {
      backgroundColor: theme.color.basis3,
    },
  }
));

function TextArea({
  cols,
  disabled,
  label,
  lightContent,
  name,
  maxLength,
  placeholder,
  onChange,
  readOnly,
  required,
  rows,
  size,
  value,
}: Props) {
  return (
    <TextAreaWrapper>
      {label && (
        <FormLabel htmlFor={name} lightContent={lightContent} required={required}>
          {label}
        </FormLabel>
      )}
      <TextField
        id={name}
        name={name}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        value={value}
        maxLength={maxLength}
        disabled={disabled}
        readOnly={readOnly}
        rows={rows}
        cols={cols}
        size={size}
        lightContent={lightContent}
      />
    </TextAreaWrapper>
  );
}

TextArea.defaultProps = defaultProps;

export default TextArea;
